import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  SparklesIcon,
  XCircleIcon,
  UserGroupIcon,
  UserCircleIcon,
  LockClosedIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  DocumentArrowUpIcon,
  DocumentTextIcon,
  XMarkIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';

/**
 * JobCreationForm - Reusable component for creating job postings
 *
 * @param {Object} props
 * @param {Object} props.formData - Form data object
 * @param {Function} props.setFormData - Function to update form data
 * @param {boolean} props.showForm - Whether to show the form or the AI generation prompt
 * @param {Function} props.setShowForm - Function to toggle form visibility
 * @param {Function} props.onJobCreated - Callback when job is created successfully (receives job object)
 * @param {boolean} props.hideBackButton - Optional: Hide the back button in form mode
 * @param {string} props.stepLabel - Optional: Custom step label (default: "Step 1 of 6: Job Details")
 */
const JobCreationForm = ({
  formData,
  setFormData,
  showForm,
  setShowForm,
  onJobCreated,
  hideBackButton = false,
  stepLabel = 'Step 1 of 6: Job Details',
}) => {
  const { user } = useAuth();
  const isGoColors = user?.orgSlug === 'go-colors---go-fashion-india-ltd-sdf61l';
  const [mode, setMode] = useState('ai'); // 'ai' | 'upload'
  const [candidateDescription, setCandidateDescription] = useState('');
  const [loadingAIGeneration, setLoadingAIGeneration] = useState(false);
  const [jdFile, setJdFile] = useState(null);
  const [uploadingJD, setUploadingJD] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAccessLevel, setShowAccessLevel] = useState(false);
  const [accessLevel, setAccessLevel] = useState('shared');
  const [accessOptions, setAccessOptions] = useState(null);
  const [loadingAccessOptions, setLoadingAccessOptions] = useState(false);
  const [selectedSharedUserIds, setSelectedSharedUserIds] = useState(new Set());
  const [selectedSharedDepartmentIds, setSelectedSharedDepartmentIds] = useState(new Set());
  const jdFileInputRef = useRef(null);

  // Shared input styling using electric blue (#007BFF)
  const inputClass =
    'w-full px-2 py-1 text-sm text-gray-900 placeholder:text-gray-400 bg-white border border-gray-300 rounded-lg transition-colors focus:ring-2 focus:ring-[#007BFF]/25 focus:border-[#007BFF] focus:outline-none';
  const [skillInput, setSkillInput] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // Collapsible sections in Review & Edit form (all closed by default)
  const [expandedSections, setExpandedSections] = useState({
    jobOverview: false,
    keyResponsibilities: false,
    experience: false,
    skills: false,
    keyAttributes: false,
  });
  const toggleSection = useCallback((key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const collapseTransition = { duration: 0.3, ease: 'easeInOut' };

  const MAX_FILE_SIZE_MB = 10;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const validateJdFile = (file) => {
    const lowerName = (file.name || '').toLowerCase();
    const isPdf = lowerName.endsWith('.pdf');
    const isDocx = lowerName.endsWith('.docx');
    const isDoc = lowerName.endsWith('.doc');
    if (!isPdf && !isDocx && !isDoc) {
      toast.error('Please upload a PDF, DOC, or DOCX file');
      return false;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File size must be under ${MAX_FILE_SIZE_MB}MB`);
      return false;
    }
    return true;
  };

  const toStrArray = (v) => {
    if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
    if (v == null || v === '') return [];
    return String(v)
      .split(/[,;\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const normalizeListText = (text) => {
    if (!text || typeof text !== 'string') return '';

    // The AI generates patterns like "Item one.,Item two." or "Item one., Item two."
    // We want to split on ".," when it separates distinct list items (followed by capital letter),
    // not when it's in the middle of a sentence with lowercase continuation

    let cleaned = text;

    // Replace ".," followed by optional space and capital letter (new list item) with ".\n"
    // This handles both ".,A" and "., A" patterns
    cleaned = cleaned.replace(/\.\s*,\s*(?=[A-Z])/g, '.\n');

    // Also replace ".," at the end of lines or strings
    cleaned = cleaned.replace(/\.\s*,\s*$/gm, '.');
    cleaned = cleaned.replace(/\.\s*,\s*\n/g, '.\n');

    // Clean up multiple newlines
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');

    return cleaned.trim();
  };

  const handleGenerateFromDescription = async () => {
    if (!candidateDescription.trim()) {
      toast.error("Please describe the candidate you're looking for");
      return;
    }

    if (candidateDescription.trim().length < 10) {
      toast.error('Please provide a more detailed description (at least 10 characters)');
      return;
    }

    try {
      setLoadingAIGeneration(true);
      const response = await axios.post('/api/job-posts/generate-from-description', {
        candidateDescription: candidateDescription.trim(),
      });

      if (response.data.success && response.data.jobPosting) {
        const generated = response.data.jobPosting;
        setFormData({
          title: generated.title || '',
          description: generated.description || '',
          keyResponsibilities: normalizeListText(generated.keyResponsibilities),
          experience: generated.experience || '',
          skills: toStrArray(generated.skills),
          keyAttributes: normalizeListText(generated.keyAttributes),
          location: generated.location || '',
          locationType: generated.locationType || 'remote',
          noticePeriod: generated.noticePeriod || '30 days',
          experienceMin: generated.experienceMin || '',
          experienceMax: generated.experienceMax || '',
          salaryMin: generated.salaryMin || '',
          salaryMax: generated.salaryMax || '',
          salaryCurrency: generated.salaryCurrency || 'INR',
          education: '',
          candidateQualification: generated.candidateQualification || '',
          genderPreference: generated.genderPreference || 'any',
          ageMin: generated.ageMin || '',
          ageMax: generated.ageMax || '',
        });
        setShowForm(true);
        toast.success('Job posting generated successfully! Review and adjust as needed.');
      }
    } catch (error) {
      console.error('Error generating job posting:', error);
      toast.error('Failed to generate job posting. Please try again.');
    } finally {
      setLoadingAIGeneration(false);
    }
  };

  const handleJdFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (!validateJdFile(file)) return;
    setJdFile(file);
  };

  const handleJdFileDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!validateJdFile(file)) return;
    setJdFile(file);
    if (jdFileInputRef.current) jdFileInputRef.current.value = '';
  };

  const handleJdDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleJdDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleJdRemoveFile = () => {
    setJdFile(null);
    if (jdFileInputRef.current) jdFileInputRef.current.value = '';
  };

  const handleGenerateFromJD = async () => {
    if (!jdFile) {
      toast.error('Please upload a Job Overview file first');
      return;
    }

    try {
      setUploadingJD(true);
      const formDataUpload = new FormData();
      formDataUpload.append('jdFile', jdFile);

      const response = await axios.post('/api/job-posts/generate-from-jd', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success && response.data.jobPosting) {
        const generated = response.data.jobPosting;
        setFormData({
          title: generated.title || '',
          description: generated.description || '',
          keyResponsibilities: normalizeListText(generated.keyResponsibilities),
          experience: generated.experience || '',
          skills: toStrArray(generated.skills),
          keyAttributes: normalizeListText(generated.keyAttributes),
          location: generated.location || '',
          locationType: generated.locationType || 'remote',
          noticePeriod: generated.noticePeriod || '30 days',
          experienceMin: generated.experienceMin || '',
          experienceMax: generated.experienceMax || '',
          salaryMin: generated.salaryMin || '',
          salaryMax: generated.salaryMax || '',
          salaryCurrency: generated.salaryCurrency || 'INR',
          education: generated.education || '',
          candidateQualification: generated.candidateQualification || '',
          genderPreference: generated.genderPreference || 'any',
          ageMin: generated.ageMin || '',
          ageMax: generated.ageMax || '',
        });
        setShowForm(true);
        toast.success('JD processed successfully! Review and adjust the details.');
      } else {
        toast.error(response.data.message || 'Failed to generate job posting from JD');
      }
    } catch (error) {
      console.error('Error generating job posting from JD:', error);
      toast.error(error.response?.data?.message || 'Failed to generate job posting from JD');
    } finally {
      setUploadingJD(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Fetch access options when form is shown (to know isOrgAdmin for flow) or when Step 2 is shown
  useEffect(() => {
    if (!showForm && !showAccessLevel) return;
    let cancelled = false;
    const fetchOptions = async () => {
      setLoadingAccessOptions(true);
      try {
        const res = await axios.get('/api/job-posts/access-options');
        if (cancelled) return;
        const data = res.data || {};
        setAccessOptions(data);
        if (showAccessLevel && accessLevel === 'private') {
          const initial = new Set();
          if (data.isOrgAdmin && Array.isArray(data.departmentAdmins)) {
            data.departmentAdmins.forEach((da) => initial.add(da._id));
          }
          if (data.isDeptAdmin && Array.isArray(data.orgAdmins)) {
            data.orgAdmins.forEach((o) => initial.add(o._id));
          }
          setSelectedSharedUserIds(initial);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching access options:', err);
          toast.error('Could not load sharing options');
        }
      } finally {
        if (!cancelled) setLoadingAccessOptions(false);
      }
    };
    fetchOptions();
    return () => {
      cancelled = true;
    };
  }, [showForm, showAccessLevel, accessLevel]);

  const toggleSharedUser = useCallback((userId) => {
    setSelectedSharedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  const toggleSharedDepartment = useCallback((deptId) => {
    setSelectedSharedDepartmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(deptId)) next.delete(deptId);
      else next.add(deptId);
      return next;
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.description) {
      toast.error('Please fill in job title and description');
      return;
    }

    if (
      showAccessLevel &&
      accessLevel === 'shared' &&
      accessOptions?.isOrgAdmin &&
      Array.isArray(accessOptions.departments) &&
      accessOptions.departments.length > 0 &&
      selectedSharedDepartmentIds.size === 0
    ) {
      toast.error('Select at least one team to share this job with.');
      return;
    }

    try {
      setSubmitting(true);
      const { experienceMin, experienceMax, salaryMin, salaryMax, ageMin, ageMax, ...rest } =
        formData;
      const payload = {
        ...rest,
        skills: toStrArray(rest.skills),
        experienceMin: experienceMin === '' ? undefined : Number(experienceMin),
        experienceMax: experienceMax === '' ? undefined : Number(experienceMax),
        salaryMin: salaryMin === '' ? undefined : Number(salaryMin),
        salaryMax: salaryMax === '' ? undefined : Number(salaryMax),
        ageMin: ageMin === '' ? undefined : Number(ageMin),
        ageMax: ageMax === '' ? undefined : Number(ageMax),
        accessLevel:
          accessLevel === 'private' && (accessOptions?.isOrgAdmin || accessOptions?.isDeptAdmin)
            ? 'private'
            : 'shared',
        sharedWithUserIds:
          accessLevel === 'private' && (accessOptions?.isOrgAdmin || accessOptions?.isDeptAdmin)
            ? [] // Private = only creator, no shared users
            : undefined,
        sharedWithDepartmentIds:
          accessLevel === 'shared' &&
          accessOptions?.isOrgAdmin &&
          selectedSharedDepartmentIds.size > 0
            ? Array.from(selectedSharedDepartmentIds)
            : undefined,
        storeFormat: isGoColors ? formData.storeFormat || 'EBO' : undefined,
      };

      const response = await axios.post('/api/job-posts/jobs', payload);
      const createdJob = response.data.job;

      toast.success('Job created successfully!');

      // Call the callback with the created job
      if (onJobCreated) {
        onJobCreated(createdJob);
      }
    } catch (error) {
      console.error('Error creating job:', error);
      toast.error(error.response?.data?.message || 'Failed to create job');
    } finally {
      setSubmitting(false);
    }
  };

  // Access Level Selection Step
  if (showAccessLevel) {
    const isPrivate = accessLevel === 'private';

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/80">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Access Level</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Step 2 of 2: Configure who can see this job
                </p>
              </div>
              {!hideBackButton && (
                <button
                  type="button"
                  onClick={() => setShowAccessLevel(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          <div className="p-5 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Access Level (required)
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label
                  className={`relative flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    accessLevel === 'shared'
                      ? 'border-[#007BFF] bg-[#007BFF]/5 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <input
                    type="radio"
                    name="accessLevel"
                    value="shared"
                    checked={accessLevel === 'shared'}
                    onChange={() => setAccessLevel('shared')}
                    className="mt-1 w-4 h-4 text-[#007BFF] border-gray-300 focus:ring-[#007BFF]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <UserGroupIcon className="h-5 w-5 text-[#007BFF] flex-shrink-0" />
                      <span className="font-medium text-gray-900">Shared</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Select which teams can see this job
                    </p>
                  </div>
                </label>

                <label
                  className={`relative flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    accessLevel === 'private'
                      ? 'border-[#007BFF] bg-[#007BFF]/5 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <input
                    type="radio"
                    name="accessLevel"
                    value="private"
                    checked={accessLevel === 'private'}
                    onChange={() => setAccessLevel('private')}
                    className="mt-1 w-4 h-4 text-[#007BFF] border-gray-300 focus:ring-[#007BFF]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <LockClosedIcon className="h-5 w-5 text-[#007BFF] flex-shrink-0" />
                      <span className="font-medium text-gray-900">Private</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">Only you can see this job</p>
                  </div>
                </label>
              </div>
            </div>

            {accessLevel === 'shared' && accessOptions?.isOrgAdmin && (
              <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                <div className="px-5 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-900">Shared with teams</h4>
                  <p className="text-xs text-gray-500 mt-1">
                    Select one or more teams. Only those teams can see this job.
                  </p>
                </div>
                <div className="p-4 max-h-[260px] overflow-y-auto">
                  {loadingAccessOptions || !Array.isArray(accessOptions.departments) ? (
                    <div className="flex flex-col items-center justify-center py-10 gap-3">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#007BFF] border-t-transparent" />
                      <span className="text-sm text-gray-500">Loading teams…</span>
                    </div>
                  ) : accessOptions.departments.length === 0 ? (
                    <div className="text-center py-8 px-4 rounded-lg bg-gray-50 border border-gray-100">
                      <BuildingOffice2Icon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">
                        No teams yet. Create teams in Organization to share jobs.
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-2">
                      {accessOptions.departments.map((dept) => (
                        <label
                          key={dept._id}
                          className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            selectedSharedDepartmentIds.has(dept._id)
                              ? 'border-[#007BFF] bg-[#007BFF]/5 shadow-sm'
                              : 'border-gray-100 bg-gray-50/50 hover:border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSharedDepartmentIds.has(dept._id)}
                            onChange={() => toggleSharedDepartment(dept._id)}
                            className="sr-only"
                          />
                          <div
                            className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              selectedSharedDepartmentIds.has(dept._id)
                                ? 'border-[#007BFF] bg-[#007BFF]'
                                : 'border-gray-300 bg-white'
                            }`}
                          >
                            {selectedSharedDepartmentIds.has(dept._id) && (
                              <svg
                                className="w-3 h-3 text-white"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>
                          <BuildingOffice2Icon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900">{dept.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowAccessLevel(false)}
                className="px-4 py-2.5 text-sm font-medium text-[#007BFF] bg-white border border-[#007BFF] rounded-lg hover:bg-[#007BFF]/5 flex items-center gap-2 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                data-testid="submit-job-btn"
                disabled={
                  submitting ||
                  (accessLevel === 'shared' &&
                    accessOptions?.isOrgAdmin &&
                    (loadingAccessOptions ||
                      (Array.isArray(accessOptions?.departments) &&
                        accessOptions.departments.length > 0 &&
                        selectedSharedDepartmentIds.size === 0)))
                }
                className="px-6 py-2.5 text-sm font-medium text-white bg-[#007BFF] rounded-lg hover:bg-[#8472B3] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Creating…
                  </>
                ) : (
                  <>
                    Create Job
                    <ArrowRightIcon className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If form is shown, display the populated form
  if (showForm) {
    return (
      <div className="space-y-3">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Review & Edit Job Details</h3>
              {/* <p className="text-xs text-gray-500 mt-0.5">{stepLabel}</p> */}
            </div>
            {!hideBackButton && (
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
                aria-label="Close"
              >
                X
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Job Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  required
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleFormChange}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection('jobOverview')}
                className="w-full flex items-center justify-between px-4 py-2.5 text-left bg-gray-50 hover:bg-gray-100 border-b border-gray-200 last:border-b-0 transition-colors"
              >
                <span className="text-xs font-medium text-gray-700">Job Overview *</span>
                {expandedSections.jobOverview ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                )}
              </button>
              <AnimatePresence initial={false}>
                {expandedSections.jobOverview && (
                  <motion.div
                    key="jobOverview"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={collapseTransition}
                    className="overflow-hidden"
                  >
                    <div className="p-3 pt-2">
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleFormChange}
                        required
                        className={`${inputClass} resize-none min-h-[4.5rem] [field-sizing:content] !border-0`}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection('keyResponsibilities')}
                className="w-full flex items-center justify-between px-4 py-2.5 text-left bg-gray-50 hover:bg-gray-100 border-b border-gray-200 last:border-b-0 transition-colors"
              >
                <span className="text-xs font-medium text-gray-700">Key Responsibilities</span>
                {expandedSections.keyResponsibilities ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                )}
              </button>
              <AnimatePresence initial={false}>
                {expandedSections.keyResponsibilities && (
                  <motion.div
                    key="keyResponsibilities"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={collapseTransition}
                    className="overflow-hidden"
                  >
                    <div className="p-3 pt-2">
                      <textarea
                        name="keyResponsibilities"
                        value={formData.keyResponsibilities}
                        onChange={handleFormChange}
                        className={`${inputClass} resize-none min-h-[4.5rem] [field-sizing:content] !border-0`}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection('experience')}
                className="w-full flex items-center justify-between px-4 py-2.5 text-left bg-gray-50 hover:bg-gray-100 border-b border-gray-200 last:border-b-0 transition-colors"
              >
                <span className="text-xs font-medium text-gray-700">Experience</span>
                {expandedSections.experience ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                )}
              </button>
              <AnimatePresence initial={false}>
                {expandedSections.experience && (
                  <motion.div
                    key="experience"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={collapseTransition}
                    className="overflow-hidden"
                  >
                    <div className="p-3 pt-2">
                      <textarea
                        name="experience"
                        value={formData.experience}
                        onChange={handleFormChange}
                        className={`${inputClass} resize-none min-h-[3rem] [field-sizing:content] !border-0`}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection('skills')}
                className="w-full flex items-center justify-between px-4 py-2.5 text-left bg-gray-50 hover:bg-gray-100 border-b border-gray-200 last:border-b-0 transition-colors"
              >
                <span className="text-xs font-medium text-gray-700">Skills</span>
                {expandedSections.skills ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                )}
              </button>
              <AnimatePresence initial={false}>
                {expandedSections.skills && (
                  <motion.div
                    key="skills"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={collapseTransition}
                    className="overflow-hidden"
                  >
                    <div className="p-3 pt-2">
                      <div className="flex flex-wrap gap-1.5 p-2 min-h-[42px] bg-white rounded-lg focus-within:ring-2 focus-within:ring-[#007BFF]/25 focus-within:ring-inset transition-colors">
                        {toStrArray(formData.skills).map((skill, i) => (
                          <span
                            key={`${skill}-${i}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#007BFF]/10 text-[#007BFF] text-xs font-medium"
                          >
                            {skill}
                            <button
                              type="button"
                              onClick={() => {
                                const next = toStrArray(formData.skills).filter((_, j) => j !== i);
                                setFormData((prev) => ({ ...prev, skills: next }));
                              }}
                              className="hover:text-[#007BFF] focus:outline-none"
                              aria-label={`Remove ${skill}`}
                            >
                              <XCircleIcon className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        ))}
                        <input
                          type="text"
                          value={skillInput}
                          onChange={(e) => setSkillInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',') {
                              e.preventDefault();
                              const s = skillInput.trim();
                              if (s && !toStrArray(formData.skills).includes(s)) {
                                setFormData((prev) => ({
                                  ...prev,
                                  skills: [...toStrArray(prev.skills), s],
                                }));
                                setSkillInput('');
                              }
                            }
                          }}
                          onBlur={() => {
                            const s = skillInput.trim();
                            if (s && !toStrArray(formData.skills).includes(s)) {
                              setFormData((prev) => ({ ...prev, skills: [...toStrArray(prev.skills), s] }));
                              setSkillInput('');
                            }
                          }}
                          placeholder="Add skill..."
                          className="flex-1 min-w-[100px] text-sm border-0 p-0 focus:ring-0 focus:outline-none"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection('keyAttributes')}
                className="w-full flex items-center justify-between px-4 py-2.5 text-left bg-gray-50 hover:bg-gray-100 border-b border-gray-200 last:border-b-0 transition-colors"
              >
                <span className="text-xs font-medium text-gray-700">Key Attributes</span>
                {expandedSections.keyAttributes ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                )}
              </button>
              <AnimatePresence initial={false}>
                {expandedSections.keyAttributes && (
                  <motion.div
                    key="keyAttributes"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={collapseTransition}
                    className="overflow-hidden"
                  >
                    <div className="p-3 pt-2">
                      <textarea
                        name="keyAttributes"
                        value={formData.keyAttributes}
                        onChange={handleFormChange}
                        className={`${inputClass} resize-none min-h-[3rem] [field-sizing:content] !border-0`}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Location Type
                </label>
                <select
                  name="locationType"
                  value={formData.locationType}
                  onChange={handleFormChange}
                  className={inputClass}
                >
                  <option value="remote">Remote</option>
                  <option value="onsite">On-site</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              {isGoColors && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Store Format
                  </label>
                  <select
                    name="storeFormat"
                    value={formData.storeFormat || 'EBO'}
                    onChange={handleFormChange}
                    className={inputClass}
                  >
                    <option value="EBO">EBO</option>
                    <option value="LFS">LFS</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Min Experience (years)
                </label>
                <input
                  type="number"
                  name="experienceMin"
                  value={formData.experienceMin}
                  onChange={handleFormChange}
                  min="0"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Max Experience (years)
                </label>
                <input
                  type="number"
                  name="experienceMax"
                  value={formData.experienceMax}
                  onChange={handleFormChange}
                  min="0"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Notice Period
                </label>
                <select
                  name="noticePeriod"
                  value={formData.noticePeriod || ''}
                  onChange={handleFormChange}
                  className={inputClass}
                >
                  <option value="">Select notice period</option>
                  <option value="Immediate">Immediate</option>
                  <option value="15 days">15 days</option>
                  <option value="30 days">30 days</option>
                  <option value="60 days">60 days</option>
                  <option value="90 days">90 days</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Min Salary per year (annual)
                </label>
                <input
                  type="number"
                  name="salaryMin"
                  value={formData.salaryMin}
                  onChange={handleFormChange}
                  min="0"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Max Salary per year (annual)
                </label>
                <input
                  type="number"
                  name="salaryMax"
                  value={formData.salaryMax}
                  onChange={handleFormChange}
                  min="0"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
                <select
                  name="salaryCurrency"
                  value={formData.salaryCurrency}
                  onChange={handleFormChange}
                  className={inputClass}
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="INR">INR</option>
                  <option value="CAD">CAD</option>
                  <option value="AUD">AUD</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Candidate Qualification
              </label>
              <input
                type="text"
                name="candidateQualification"
                value={formData.candidateQualification}
                onChange={handleFormChange}
                placeholder="e.g., Bachelor's in Computer Science, MBA"
                className={inputClass}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              {!hideBackButton && (
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2.5 text-sm bg-white text-[#007BFF] border border-[#007BFF] rounded-lg hover:bg-[#007BFF]/5 flex items-center gap-2 font-medium transition-colors"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  <span>Back</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (accessOptions?.isOrgAdmin) {
                    setShowAccessLevel(true);
                  } else {
                    handleSubmit({ preventDefault: () => {} });
                  }
                }}
                data-testid="continue-to-access-btn"
                disabled={loadingAccessOptions && !accessOptions}
                className="px-6 py-2.5 text-sm bg-[#007BFF] text-white rounded-lg hover:bg-[#8472B3] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium transition-colors"
              >
                {loadingAccessOptions && !accessOptions ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    <span>Loading…</span>
                  </>
                ) : (
                  <>
                    <span>Continue</span>
                    <ArrowRightIcon className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Initial prompt view with mode selection
  return (
    <div className="space-y-6">
      {/* Mode Selection – polished segmented control */}
      <div className="flex justify-center">
        <div
          role="tablist"
          aria-label="Job creation method"
          className="inline-flex rounded-xl bg-gray-100/80 p-1 shadow-inner"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'ai'}
            onClick={() => setMode('ai')}
            className={`relative px-4 py-2.5 text-sm font-medium rounded-lg flex items-center gap-2 transition-all duration-200 ${
              mode === 'ai'
                ? 'bg-white text-[#007BFF] shadow-md'
                : 'text-gray-600 hover:text-[#8472B3] hover:bg-[#8472B3]/10'
            }`}
          >
            <SparklesIcon className="h-4 w-4" />
            <span>AI Magic</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'upload'}
            onClick={() => setMode('upload')}
            data-testid="upload-jd-tab"
            className={`relative px-4 py-2.5 text-sm font-medium rounded-lg flex items-center gap-2 transition-all duration-200 ${
              mode === 'upload'
                ? 'bg-white text-[#007BFF] shadow-md'
                : 'text-gray-600 hover:text-[#8472B3] hover:bg-[#8472B3]/10'
            }`}
          >
            <DocumentTextIcon className="h-4 w-4" />
            <span>Upload JD</span>
          </button>
        </div>
      </div>

      {/* Content with transition */}
      <div className="max-w-2xl mx-auto">
        <AnimatePresence mode="wait">
          {mode === 'ai' ? (
            <motion.div
              key="ai"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200/80 shadow-sm"
            >
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-[#007BFF] rounded-2xl mb-4 shadow-lg shadow-[#007BFF]/25">
                  <SparklesIcon className="h-7 w-7 text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-1.5">
                  Describe Your Ideal Candidate
                </h2>
                <p className="text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
                  Tell us about the candidate you&apos;re looking for — skills, experience,
                  location. Our AI will create a tailored job posting.
                </p>
              </div>

              <div className="space-y-4">
                <textarea
                  value={candidateDescription}
                  onChange={(e) => setCandidateDescription(e.target.value)}
                  placeholder="e.g., Senior React developer, 5+ years experience, strong in JavaScript and Node.js, remote from India. Experience with modern frontend frameworks and agile processes..."
                  rows={5}
                  className={`${inputClass} rounded-xl resize-none py-3`}
                />

                <button
                  type="button"
                  onClick={handleGenerateFromDescription}
                  data-testid="generate-job-ai-btn"
                  disabled={loadingAIGeneration || !candidateDescription.trim()}
                  className="w-full px-6 py-3.5 bg-[#007BFF] text-white rounded-xl font-semibold text-sm shadow-lg shadow-[#007BFF]/25 hover:bg-[#8472B3] hover:shadow-[#007BFF]/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 transition-all duration-200"
                >
                  {loadingAIGeneration ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      <span>Generating your job posting...</span>
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="h-5 w-5" />
                      <span>Generate Job Posting with AI</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200/80 shadow-sm"
            >
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-[#007BFF] rounded-2xl mb-4 shadow-lg shadow-[#007BFF]/25">
                  <DocumentTextIcon className="h-7 w-7 text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-1.5">
                  Upload Existing Job Description
                </h2>
                <p className="text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
                  Upload your JD in PDF or Word format. We&apos;ll extract key details and pre-fill
                  the form — you can review and edit before publishing.
                </p>
              </div>

              <div className="space-y-4">
                <label className="block text-sm font-semibold text-gray-800">
                  Upload JD (PDF, DOC, DOCX)
                </label>
                <div
                  onDragOver={handleJdDragOver}
                  onDragLeave={handleJdDragLeave}
                  onDrop={handleJdFileDrop}
                  onClick={() => jdFileInputRef.current?.click()}
                  className={`relative flex flex-col items-center justify-center w-full rounded-xl border-2 border-dashed px-6 py-8 cursor-pointer transition-all duration-200 ${
                    isDragging
                      ? 'border-[#007BFF] bg-[#007BFF]/5'
                      : jdFile
                        ? 'border-emerald-300 bg-emerald-50/50'
                        : 'border-gray-300 bg-gray-50/50 hover:border-[#007BFF] hover:bg-[#007BFF]/5'
                  }`}
                >
                  <input
                    id="jd-file-input"
                    ref={jdFileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleJdFileChange}
                    className="sr-only"
                    tabIndex={-1}
                  />
                  {jdFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center gap-3 px-4 py-2 bg-white rounded-lg border border-emerald-200 shadow-sm">
                        <DocumentTextIcon className="h-8 w-8 text-[#007BFF] flex-shrink-0" />
                        <div className="text-left min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                            {jdFile.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(jdFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleJdRemoveFile();
                          }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          aria-label="Remove file"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">
                        Click to replace or drop a different file
                      </p>
                    </div>
                  ) : (
                    <>
                      <DocumentArrowUpIcon className="h-10 w-10 text-gray-400 mb-2" />
                      <p className="text-sm font-medium text-gray-700">
                        {isDragging
                          ? 'Drop your file here'
                          : 'Drop your file here or click to browse'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Max {MAX_FILE_SIZE_MB}MB · PDF, DOC, DOCX
                      </p>
                    </>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleGenerateFromJD}
                  data-testid="generate-job-jd-btn"
                  disabled={uploadingJD || !jdFile}
                  className="w-full px-6 py-3.5 bg-[#007BFF] text-white rounded-xl font-semibold text-sm shadow-lg shadow-[#007BFF]/25 hover:bg-[#8472B3] hover:shadow-[#007BFF]/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 transition-all duration-200"
                >
                  {uploadingJD ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      <span>Processing JD and generating...</span>
                    </>
                  ) : (
                    <>
                      <DocumentTextIcon className="h-5 w-5" />
                      <span>Generate Job Posting from Uploaded JD</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default JobCreationForm;
